import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useUpdateInstallationStageStatus } from '../../../../app/ApiGen';
import { Dialog } from '../../../../components/Dialog';
import { errorSnackbar } from '../../../../components/ErrorSnackbar';
import { TextField } from '../../../../components/TextField';

export interface CommissioningDetails {
  chargePointId: string;
  installerId: string;
  supplyCurrentLimit: string;
}

export interface CompleteCommissioningDialogProps {
  defaults: CommissioningDetails;
  installers: { id: string; label: string }[];
  isOpen: boolean;
  jobId: string;
  onClose: () => void;
  onCompleted: () => Promise<unknown>;
}

export const CompleteCommissioningDialog: React.VFC<CompleteCommissioningDialogProps> = ({
  defaults,
  installers,
  isOpen,
  jobId,
  onClose,
  onCompleted,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { mutate, loading } = useUpdateInstallationStageStatus({ jobId, stage: 'commissioning' });
  const [details, setDetails] = React.useState<CommissioningDetails>(defaults);

  React.useEffect(() => {
    if (isOpen) {
      setDetails(defaults);
    }
  }, [defaults, isOpen]);

  const supplyCurrentLimit = Number(details.supplyCurrentLimit);
  const isValid =
    details.chargePointId.trim() !== '' &&
    details.supplyCurrentLimit.trim() !== '' &&
    supplyCurrentLimit > 0 &&
    details.installerId !== '';

  const handleSubmit = () => {
    mutate({
      data: {
        id: jobId,
        type: 'jobs',
        attributes: { status: 'Completed', supplyCurrentLimit },
        relationships: {
          installedChargePoint: { data: { type: 'chargePoints', id: details.chargePointId.trim() } },
          installer: { data: { type: 'users', id: details.installerId } },
        },
      },
    })
      .then(async () => {
        onClose();
        await onCompleted();
        enqueueSnackbar('Commissioning completed', { variant: 'success' });
      })
      .catch((err) => {
        errorSnackbar('Unable to complete commissioning', closeSnackbar, enqueueSnackbar, err);
      });
  };

  return (
    <Dialog
      hasCancelButton
      hasCloseButton={false}
      isOpen={isOpen}
      onClose={onClose}
      submitButton={
        <Button color="warning" disabled={!isValid || loading} onClick={handleSubmit} variant="contained">
          Complete commissioning
        </Button>
      }
      title="Complete commissioning"
    >
      <p>
        Marks the Commissioning stage as Completed and records these details on the job. Nothing is sent to the charge
        point. The next stage becomes Ready so the installer can finish the job, and a hardware-only job is completed.
      </p>
      <Box display="flex" flexDirection="column" gap={2} py={1}>
        <TextField
          fullWidth
          label="Charge point ID"
          onChange={(e) => setDetails({ ...details, chargePointId: e.target.value })}
          value={details.chargePointId}
        />
        <TextField
          fullWidth
          label="Supply current limit (A)"
          onChange={(e) => setDetails({ ...details, supplyCurrentLimit: e.target.value })}
          type="number"
          value={details.supplyCurrentLimit}
        />
        <TextField
          fullWidth
          label="Installer"
          onChange={(e) => setDetails({ ...details, installerId: e.target.value })}
          select
          value={details.installerId}
          variant="outlined"
        >
          {installers.map(({ id, label }) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Dialog>
  );
};
